import React, { useState, useEffect } from 'react';
import './ProductPickerModal.css';
import { X, ImageOff } from 'lucide-react';
import { productsAPI } from '../../../config/product-api';
import { formatPrice } from '../../../utils/orderDisplay';

const VariantSelectorModal = ({ isOpen, onClose, product, parentSelectedItems, onConfirm, onRemove, adminData }) => {
  const [selectedAttributes, setSelectedAttributes] = useState({});
  const [qty, setQty] = useState(1);

  // Derive all unique option names and their unique values
  const optionNames = [];
  if (product && product.variants) {
    product.variants.forEach((v) => {
      v.attributes?.forEach((attr) => {
        if (!optionNames.includes(attr.option_name)) {
          optionNames.push(attr.option_name);
        }
      });
    });
  }

  const optionValues = {};
  optionNames.forEach((name) => {
    const valuesList = [];
    product.variants.forEach((v) => {
      v.attributes?.forEach((attr) => {
        if (attr.option_name === name && !valuesList.includes(attr.option_value)) {
          valuesList.push(attr.option_value);
        }
      });
    });
    optionValues[name] = valuesList;
  });

  const variantHasOptionName = (variant, optionName) => {
    return variant.attributes?.some(attr => attr.option_name === optionName) ?? false;
  };

  const variantHasOptionValue = (variant, optionName, optionValue) => {
    if (!optionValue) return false;
    return variant.attributes?.some(
      attr => attr.option_name === optionName &&
      String(attr.option_value).trim().toLowerCase() === String(optionValue).trim().toLowerCase()
    ) ?? false;
  };

  const isOptionApplicable = (variants, optionName, currentSelection) => {
    if (currentSelection[optionName]) return true;

    return variants.some(variant => {
      if (variant.isAvailable === false) return false;
      if (!variantHasOptionName(variant, optionName)) return false;

      return Object.entries(currentSelection).every(([opt, val]) => {
        if (opt === optionName || !val) return true;
        if (!variantHasOptionName(variant, opt)) return true;
        return variantHasOptionValue(variant, opt, val);
      });
    });
  };

  const isOptionValueAvailable = (variants, optionName, optionValue, currentSelection) => {
    return variants.some(variant => {
      if (variant.isAvailable === false) return false;
      if (!variantHasOptionValue(variant, optionName, optionValue)) return false;

      return Object.entries(currentSelection).every(([opt, val]) => {
        if (opt === optionName || !val) return true;
        if (!variantHasOptionName(variant, opt)) return true;
        return variantHasOptionValue(variant, opt, val);
      });
    });
  };

  // Determine currently applicable option names under selection
  const applicableOptionNames = product?.variants
    ? optionNames.filter(name => isOptionApplicable(product.variants, name, selectedAttributes))
    : [];


  // Resolve matching variant based on current attributes selection & applicable option names
  const isAllSelected = applicableOptionNames.every(optName => !!selectedAttributes[optName]);

  const matchingVariant = isAllSelected && product?.variants
    ? product.variants.find((variant) => {
        if (variant.isAvailable === false) return false;

        // Must match all applicable options that have a selection
        return applicableOptionNames.every((optName) => {
          const selectedValue = selectedAttributes[optName];
          if (!selectedValue) return false;
          if (!variantHasOptionName(variant, optName)) return false;
          return variantHasOptionValue(variant, optName, selectedValue);
        });
      })
    : undefined;
  const parentItemKey = matchingVariant ? (product?._id + '-' + matchingVariant.sku) : '';
  const alreadySelectedParentItem = parentSelectedItems[parentItemKey];
  const isAlreadySelected = !!alreadySelectedParentItem;

  useEffect(() => {
    if (isOpen && product) {
      // Find first available variant to pre-select
      const firstAvail = product.variants.find(v => v.isAvailable !== false) || product.variants[0];
      if (firstAvail) {
        const initAttrs = {};
        firstAvail.attributes?.forEach(attr => {
          initAttrs[attr.option_name] = attr.option_value;
        });
        setSelectedAttributes(initAttrs);
      }
    }
  }, [isOpen, product]);

  useEffect(() => {
    if (matchingVariant && product) {
      const parentItem = parentSelectedItems[product._id + '-' + matchingVariant.sku];
      setQty(parentItem?.qty || 1);
    } else {
      setQty(1);
    }
  }, [matchingVariant, parentSelectedItems, product]);

  if (!isOpen || !product) return null;


  const handleSelectAttribute = (optionName, value) => {
    setSelectedAttributes((prev) => {
      const cleanedSelection = { [optionName]: value };

      // For other attributes, only keep them if they are still available under this new option choice
      Object.keys(prev).forEach((opt) => {
        if (opt !== optionName && prev[opt]) {
          const isAvail = isOptionValueAvailable(product.variants, opt, prev[opt], { [optionName]: value });
          if (isAvail) {
            cleanedSelection[opt] = prev[opt];
          }
        }
      });

      // Automatically select the first available option value for newly applicable attributes
      optionNames.forEach((optName) => {
        if (!cleanedSelection[optName]) {
          const isApplicable = isOptionApplicable(product.variants, optName, cleanedSelection);
          if (isApplicable) {
            const values = optionValues[optName] || [];
            const firstAvailValue = values.find(val => isOptionValueAvailable(product.variants, optName, val, cleanedSelection));
            if (firstAvailValue) {
              cleanedSelection[optName] = firstAvailValue;
            }
          }
        }
      });

      return cleanedSelection;
    });
  };
  const handleConfirm = () => {
    if (!matchingVariant) return;
    const customizedVariant = {
      ...matchingVariant,
      attributes: Object.entries(selectedAttributes).map(([name, val]) => ({
        option_name: name,
        option_value: val
      }))
    };
    onConfirm(customizedVariant, qty);
  };

  const handleRemove = () => {
    if (!matchingVariant) return;
    onRemove(matchingVariant);
  };

  return (
    <div className="variant-picker-overlay" onClick={onClose}>
      <div className="variant-picker-modal" onClick={(e) => e.stopPropagation()}>
        <div className="variant-picker-header">
          <h3>Select Variant</h3>
          <button className="variant-picker-close-btn" onClick={onClose} aria-label="Close variant selector">
            <X size={18} />
          </button>
        </div>
        <div className="variant-picker-body">
          <div className="variant-picker-product-info">
            {product.images?.[0]?.thumbnail || product.images?.[0]?.image ? (
              <img
                src={product.images?.[0]?.thumbnail || product.images?.[0]?.image}
                alt={product.name}
                className="variant-picker-product-thumb"
              />
            ) : (
              <div className="variant-picker-product-thumb-fallback">
                <ImageOff size={16} />
              </div>
            )}
            <span className="variant-picker-product-name">{product.name}</span>
          </div>

          {/* Attributes Selectors */}
          <div className="variant-picker-attributes-section">
            {optionNames.map((optionName) => {
              const isApplicable = isOptionApplicable(product.variants, optionName, selectedAttributes);
              if (!isApplicable) return null;

              const values = optionValues[optionName] || [];
              return (
                <div key={optionName} className="variant-picker-attribute-row">
                  <span className="variant-picker-attribute-label capitalize">{optionName}:</span>
                  <div className="variant-picker-attribute-options">
                    {values.map((val) => {
                      const isSelected = selectedAttributes[optionName] === val;
                      const isValAvail = isOptionValueAvailable(product.variants, optionName, val, selectedAttributes);

                      return (
                        <button
                          key={val}
                          type="button"
                          disabled={!isValAvail}
                          className={`variant-picker-attribute-btn ${isSelected ? 'is-selected' : ''}`}
                          onClick={() => handleSelectAttribute(optionName, val)}
                          style={!isValAvail ? { opacity: 0.4, cursor: 'not-allowed' } : {}}
                        >
                          {val}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Matching Variant Info Card */}
          {matchingVariant ? (
            <div className="variant-picker-resolved-card" style={{ display: 'flex', gap: '12px', padding: '12px', border: '1px solid #e2e8f0', borderRadius: '8px', backgroundColor: '#f8fafc', alignItems: 'center', marginTop: '16px' }}>
              <div className="variant-picker-resolved-img-container" style={{ flexShrink: 0 }}>
                {matchingVariant.images?.[0]?.thumbnail || matchingVariant.images?.[0]?.image ? (
                  <img
                    src={matchingVariant.images?.[0]?.thumbnail || matchingVariant.images?.[0]?.image}
                    alt={product.name}
                    style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #cbd5e1' }}
                  />
                ) : (
                  <div style={{ width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#e2e8f0', borderRadius: '6px', color: '#94a3b8' }}>
                    <ImageOff size={16} />
                  </div>
                )}
              </div>

              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px', minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '0.9rem', fontWeight: '600', color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {applicableOptionNames.map((name) => selectedAttributes[name]).filter(Boolean).join(' / ')}
                  </span>
                  <span style={{ fontSize: '0.95rem', fontWeight: '700', color: '#6366f1', flexShrink: 0 }}>
                    {formatPrice(matchingVariant.price || 0, adminData)}
                  </span>
                </div>

                {matchingVariant.isAvailable !== false && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ fontSize: '0.8rem', color: '#64748b' }}>Qty:</span>
                      <div className="product-picker-qty-selector">
                        <button
                          type="button"
                          className="product-picker-qty-btn"
                          onClick={() => setQty((prev) => Math.max(1, prev - 1))}
                        >
                          -
                        </button>
                        <input
                          type="number"
                          min="1"
                          value={qty}
                          onChange={(e) => setQty(Math.max(1, parseInt(e.target.value) || 1))}
                          className="product-picker-qty-input"
                        />
                        <button
                          type="button"
                          className="product-picker-qty-btn"
                          onClick={() => setQty((prev) => prev + 1)}
                        >
                          +
                        </button>
                      </div>
                    </div>

                    {isAlreadySelected && (
                      <button
                        type="button"
                        className="variant-picker-remove-btn"
                        onClick={handleRemove}
                        style={{ padding: '4px 8px', fontSize: '0.75rem', borderRadius: '4px' }}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="variant-picker-no-match">
              {isAllSelected
                ? "No variant matches this combination of attributes."
                : "Please select all attributes to resolve variant."}
            </div>
          )}
        </div>
        <div className="variant-picker-footer">
          <button type="button" className="variant-picker-cancel-btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="variant-picker-confirm-btn"
            disabled={!matchingVariant}
            onClick={handleConfirm}
          >
            {isAlreadySelected ? 'Update Selection' : 'Confirm Selection'}
          </button>
        </div>
      </div>
    </div>
  );
};

const ProductPickerModal = ({ isOpen, onClose, onSave, initialSelected = [], adminData }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
  });

  // Track selection state: maps product _id + variant SKU (if any) to selection object
  const [selectedItems, setSelectedItems] = useState({});
  const [variantSelectionProduct, setVariantSelectionProduct] = useState(null);

  // Sync initial selection when modal opens
  useEffect(() => {
    if (isOpen) {
      const initialMap = {};
      initialSelected.forEach((item) => {
        const key = item._id + (item.selectedVariant?.sku ? '-' + item.selectedVariant.sku : '');
        initialMap[key] = { ...item };
      });
      setSelectedItems(initialMap);
      setCurrentPage(1);
      setSearchInput('');
      setSearchQuery('');
      setError(null);
    }
  }, [isOpen, initialSelected]);

  // Set debounced searchQuery when user types
  useEffect(() => {
    const trimmed = searchInput.trim();
    const delayDebounce = setTimeout(() => {
      setSearchQuery(trimmed);
      setCurrentPage(1);
    }, 350); // 350ms debounce
    return () => clearTimeout(delayDebounce);
  }, [searchInput]);

  // Fetch products list matching search query
  useEffect(() => {
    if (!isOpen) return;

    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await productsAPI.getProductsList({
          page: currentPage,
          limit: 10,
          search: searchQuery || undefined,
          isAvailable: true, // Only show available products
        });

        if (response.success) {
          setProducts(response.data || []);
          setPagination(response.pagination || { currentPage, totalPages: 1, totalCount: response.data.length });
        } else {
          setError(response.error || 'Failed to fetch products');
        }
      } catch (err) {
        setError('Error fetching products: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [isOpen, currentPage, searchQuery]);

  if (!isOpen) return null;

  const handleSearchSubmit = (e) => {
    e.preventDefault();
  };

  const handleResetSearch = () => {
    setSearchInput('');
    setSearchQuery('');
    setCurrentPage(1);
  };

  const handleToggleSelect = (product, forceDeselect = false) => {
    const selectedVariantsOfProduct = Object.values(selectedItems).filter(item => item._id === product._id);
    const isSelected = selectedVariantsOfProduct.length > 0;

    if (isSelected || forceDeselect) {
      setSelectedItems((prev) => {
        const updated = { ...prev };
        Object.keys(updated).forEach((key) => {
          if (updated[key]._id === product._id) {
            delete updated[key];
          }
        });
        return updated;
      });
    } else {
      if (product.variants && product.variants.length > 0) {
        setVariantSelectionProduct(product);
      } else {
        setSelectedItems((prev) => {
          const updated = { ...prev };
          updated[product._id] = {
            _id: product._id,
            productId: product.productId,
            name: product.name,
            price: product.rate || 0,
            qty: 1,
            images: product.images || [],
          };
          return updated;
        });
      }
    }
  };

  const handleConfirmVariant = (selectedVariant, qty) => {
    if (!variantSelectionProduct) return;
    const key = variantSelectionProduct._id + '-' + selectedVariant.sku;
    const variantLabel = selectedVariant.attributes
      ?.map(a => `${a.option_value || ''} ${a.option_name || ''}`.trim())
      .filter(Boolean)
      .join(' | ') || '';
    const fullName = variantLabel ? `${variantSelectionProduct.name} | ${variantLabel}` : variantSelectionProduct.name;
    setSelectedItems((prev) => ({
      ...prev,
      [key]: {
        _id: variantSelectionProduct._id,
        productId: variantSelectionProduct.productId,
        name: fullName,
        price: selectedVariant.price || 0,
        qty: qty,
        images: selectedVariant.images?.length ? selectedVariant.images : (variantSelectionProduct.images || []),
        selectedVariant: selectedVariant,
      },
    }));
    setVariantSelectionProduct(null);
  };

  const handleRemoveVariant = (selectedVariant) => {
    if (!variantSelectionProduct) return;
    const key = variantSelectionProduct._id + '-' + selectedVariant.sku;
    setSelectedItems((prev) => {
      const updated = { ...prev };
      delete updated[key];
      return updated;
    });
    setVariantSelectionProduct(null);
  };

  const handleQtyChange = (productId, newQty) => {
    const qty = Math.max(1, parseInt(newQty) || 1);
    setSelectedItems((prev) => {
      if (!prev[productId]) return prev;
      return {
        ...prev,
        [productId]: {
          ...prev[productId],
          qty,
        },
      };
    });
  };

  const handleSave = () => {
    const selectionArray = Object.values(selectedItems);
    onSave(selectionArray);
    onClose();
  };

  return (
    <div className="product-picker-overlay" onClick={onClose}>
      <div className="product-picker-modal" onClick={(e) => e.stopPropagation()}>
        <div className="product-picker-header">
          <h3>Select Products</h3>
          <button className="product-picker-close-btn" onClick={onClose} aria-label="Close modal">
            <X size={20} />
          </button>
        </div>

        <form className="product-picker-search-bar" onSubmit={handleSearchSubmit}>
          <div className="product-picker-search-input-wrapper">
            <input
              type="text"
              placeholder="Search products by name..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            {searchInput && (
              <button type="button" className="product-picker-clear-search" onClick={handleResetSearch}>
                &times;
              </button>
            )}
          </div>
        </form>

        <div className="product-picker-content">
          {loading ? (
            <div className="product-picker-loading">Loading products...</div>
          ) : error ? (
            <div className="product-picker-error">{error}</div>
          ) : products.length === 0 ? (
            <div className="product-picker-empty">No products found.</div>
          ) : (
            <div className="product-picker-list">
              {products.map((product) => {
                const selectedVariantsOfProduct = Object.values(selectedItems).filter(item => item._id === product._id);
                const isSelected = selectedVariantsOfProduct.length > 0;
                const currentQty = isSelected && !product.variants?.length ? selectedItems[product._id].qty : 1;
                const imageUrl = product.images?.[0]?.thumbnail || product.images?.[0]?.image || '';
                const displayPrice = product.rate || 0;
                const variantLabels = selectedVariantsOfProduct
                  .filter((item) => item.selectedVariant)
                  .map(
                    (item) => `${(item.selectedVariant.attributes || []).map(a => a.option_value).join('/')} (${item.qty})`
                  ).join(', ');

                return (
                  <div
                    key={product._id}
                    className={`product-picker-row ${isSelected ? 'is-selected' : ''}`}
                    onClick={() => {
                      if (product.variants && product.variants.length > 0) {
                        setVariantSelectionProduct(product);
                      } else {
                        handleToggleSelect(product);
                      }
                    }}
                  >
                    <div className="product-picker-checkbox-cell" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleToggleSelect(product)}
                      />
                    </div>

                    <div className="product-picker-img-container">
                      {imageUrl ? (
                        <img src={imageUrl} alt={product.name} className="product-picker-thumb" />
                      ) : (
                        <div className="product-picker-thumb-fallback">
                          <ImageOff size={16} />
                        </div>
                      )}
                    </div>

                    <div className="product-picker-info-cell">
                      <div className="product-picker-name">{product.name}</div>
                      {isSelected && variantLabels && (
                        <div className="product-picker-selected-variant-label" style={{ fontSize: '0.8rem', color: '#64748b', margin: '2px 0' }}>
                          Selected: {variantLabels}
                        </div>
                      )}
                      <div className="product-picker-price">{formatPrice(displayPrice, adminData)}</div>
                    </div>

                    {isSelected && !product.variants?.length && (
                      <div className="product-picker-qty-cell" onClick={(e) => e.stopPropagation()}>
                        <div className="product-picker-qty-selector">
                          <button
                            type="button"
                            className="product-picker-qty-btn"
                            onClick={() => handleQtyChange(product._id, currentQty - 1)}
                          >
                            -
                          </button>
                          <input
                            type="number"
                            min="1"
                            value={currentQty}
                            onChange={(e) => handleQtyChange(product._id, e.target.value)}
                            className="product-picker-qty-input"
                          />
                          <button
                            type="button"
                            className="product-picker-qty-btn"
                            onClick={() => handleQtyChange(product._id, currentQty + 1)}
                          >
                            +
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {pagination.totalPages > 1 && (
          <div className="product-picker-pagination">
            <button
              type="button"
              disabled={currentPage <= 1 || loading}
              onClick={() => setCurrentPage((prev) => prev - 1)}
            >
              Previous
            </button>
            <span>
              Page {currentPage} of {pagination.totalPages}
            </span>
            <button
              type="button"
              disabled={currentPage >= pagination.totalPages || loading}
              onClick={() => setCurrentPage((prev) => prev + 1)}
            >
              Next
            </button>
          </div>
        )}

        <div className="product-picker-footer">
          <div className="product-picker-selected-count">
            {Object.keys(selectedItems).length === 0
              ? 'No products selected'
              : `Selected: ${Object.keys(selectedItems).length} ${Object.keys(selectedItems).length === 1 ? 'product' : 'products'}`}
          </div>
          <div className="product-picker-footer-actions">
            <button type="button" className="product-picker-cancel-btn" onClick={onClose}>
              Cancel
            </button>
            <button type="button" className="product-picker-save-btn" onClick={handleSave}>
              Save Selection
            </button>
          </div>
        </div>
      </div>

      <VariantSelectorModal
        isOpen={!!variantSelectionProduct}
        onClose={() => setVariantSelectionProduct(null)}
        product={variantSelectionProduct}
        parentSelectedItems={selectedItems}
        onConfirm={handleConfirmVariant}
        onRemove={handleRemoveVariant}
        adminData={adminData}
      />
    </div>
  );
};

export default ProductPickerModal;
